import React from "react";
import {
	Paper,
	TextField,
	Button,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	SelectChangeEvent
} from "@mui/material";
import { Search as SearchIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { ProjectStatus } from "../../models/Project";
import { Company } from "../../models/Company";
import { useEnumTranslation } from "../../utils/translateEnum";
import { zoullooColors } from "../../theme";
import CompanyAutocomplete from "../common/CompanyAutocomplete";

interface ProjectFiltersProps {
	isMobile: boolean;
	nameFilter: string;
	projectNumberFilter: string;
	statusFilter: string;
	companyFilter?: Company | null;
	showCompanyFilter: boolean;
	onNameFilterChange: (name: string) => void;
	onProjectNumberFilterChange: (projectNumber: string) => void;
	onStatusFilterChange: (status: string) => void;
	onCompanyFilterChange: (company: Company | null) => void;
	onSearch: () => void;
	onReset: () => void;
}

const ProjectFilters: React.FC<ProjectFiltersProps> = ({
	isMobile,
	nameFilter,
	projectNumberFilter,
	statusFilter,
	companyFilter,
	showCompanyFilter,
	onNameFilterChange,
	onProjectNumberFilterChange,
	onStatusFilterChange,
	onCompanyFilterChange,
	onSearch,
	onReset
}) => {
	const { t } = useTranslation();
	const { translateProjectStatus } = useEnumTranslation();

	const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
		onStatusFilterChange(event.target.value);
	};

	const handleCompanyFilterChange = (company: Company | null) => {
		onCompanyFilterChange(company);
	};

	return (
		<Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
			<Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<TextField
						fullWidth
						variant="outlined"
						label={t("project.name")}
						value={nameFilter}
						onChange={e => onNameFilterChange(e.target.value)}
						size={isMobile ? "small" : "medium"}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 3 }}>
					<TextField
						fullWidth
						variant="outlined"
						label={t("project.projectNumber")}
						value={projectNumberFilter}
						onChange={e => onProjectNumberFilterChange(e.target.value)}
						size={isMobile ? "small" : "medium"}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: showCompanyFilter ? 4 : 6, md: 2 }}>
					<FormControl fullWidth size={isMobile ? "small" : "medium"}>
						<InputLabel>{t("project.status")}</InputLabel>
						<Select
							value={statusFilter}
							onChange={handleStatusFilterChange}
							label={t("project.status")}
						>
							<MenuItem value="">{t("common.all")}</MenuItem>
							{Object.values(ProjectStatus).map(status => (
								<MenuItem key={status} value={status}>
									{translateProjectStatus(status)}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				{showCompanyFilter && (
					<Grid size={{ xs: 12, sm: 8, md: 2 }}>
						<CompanyAutocomplete
							value={companyFilter || null}
							onChange={handleCompanyFilterChange}
							label={t("project.company")}
							size="small"
							fullWidth
						/>
					</Grid>
				)}
				<Grid
					size={{
						xs: 10,
						sm: showCompanyFilter ? 10 : 5,
						md: showCompanyFilter ? 1.5 : 3.5
					}}
				>
					<Button
						fullWidth
						variant="outlined"
						onClick={onSearch}
						startIcon={<SearchIcon />}
						size={isMobile ? "medium" : "large"}
						sx={{
							height: isMobile ? "40px" : "56px",
							borderColor: zoullooColors.secondary.main,
							color: zoullooColors.secondary.main,
							backgroundColor: zoullooColors.background.default,
							"&:hover": {
								borderColor: zoullooColors.secondary.dark,
								backgroundColor: zoullooColors.secondary.alpha
							}
						}}
					>
						{t("common.search")}
					</Button>
				</Grid>
				<Grid
					size={{
						xs: 2,
						sm: showCompanyFilter ? 2 : 1,
						md: 0.5
					}}
				>
					<Button
						variant="outlined"
						onClick={onReset}
						size={isMobile ? "medium" : "large"}
						sx={{
							height: isMobile ? "40px" : "56px",
							minWidth: isMobile ? "40px" : "56px",
							width: "100%",
							borderColor: zoullooColors.grey[400],
							color: zoullooColors.grey[600],
							backgroundColor: zoullooColors.background.default,
							"&:hover": {
								borderColor: zoullooColors.grey[600],
								backgroundColor: zoullooColors.grey[100]
							}
						}}
					>
						<RefreshIcon />
					</Button>
				</Grid>
			</Grid>
		</Paper>
	);
};

export default ProjectFilters;